import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Camera, CheckCircle2, ScanLine, Search, Undo2 } from 'lucide-react';
import type { Guest } from '@peles/shared';
import { useDialog } from '../context/DialogContext';
import { useGuests } from '../hooks/useGuests';
import { useEventAttendance, useCheckInGuest, useCheckInByToken } from '../hooks/useInvites';
import { useEvent } from '../hooks/useEvents';

/**
 * Extracts the RSVP token from a scanned QR value. Accepts a bare token, the
 * RSVP page URL, and the shareable preview URL (`/i/<token>`) — guests arrive
 * at the door with whichever link they were sent.
 */
export function tokenFromScan(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const match = trimmed.match(/\/(?:rsvp|i)\/([A-Za-z0-9-_]+)/);
  if (match) return match[1];
  return /^[A-Za-z0-9-_]{16,}$/.test(trimmed) ? trimmed : null;
}

/**
 * Mobile-first event-day check-in: attendance counters, guest search list
 * with one-tap check-in/undo, and QR scanning (camera via BarcodeDetector
 * where supported, plus a manual token/link field fallback).
 */
export function CheckInPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const { alert } = useDialog();
  const { data: event } = useEvent(eventId ?? '');
  const { data: guests = [], isLoading } = useGuests(eventId ? { eventId } : undefined);
  const { data: attendance } = useEventAttendance(eventId);
  const checkInGuest = useCheckInGuest();
  const checkInByToken = useCheckInByToken();

  const [search, setSearch] = useState('');
  const [scanValue, setScanValue] = useState('');
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Navigating away mid-scan must release the camera: stop the tracks and
  // null the stream ref, which also ends the self-rescheduling detect loop.
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };
  }, []);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return guests;
    return guests.filter((guest) =>
      [guest.firstName, guest.lastName, guest.familyGroup]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query),
    );
  }, [guests, search]);

  const handleToggle = async (guest: Guest) => {
    try {
      await checkInGuest.mutateAsync({ guestId: guest.id, undo: !!guest.checkedInAt, eventId });
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Check-in failed',
        variant: 'error',
      });
    }
  };

  const handleTokenSubmit = async (raw: string) => {
    const token = tokenFromScan(raw);
    if (!token) {
      await alert({ message: 'Not a valid RSVP link or code.', variant: 'warning' });
      return;
    }
    try {
      const result = await checkInByToken.mutateAsync({ token, eventId });
      setScanValue('');
      await alert({
        message: `${result.guest.guestName} checked in${
          result.guest.tableNumber != null ? ` — Table ${result.guest.tableNumber}` : ''
        }.`,
        variant: 'success',
      });
    } catch (err) {
      await alert({
        message: err instanceof Error ? err.message : 'Check-in failed',
        variant: 'error',
      });
    }
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setScanning(false);
  };

  const startCamera = async () => {
    const DetectorCtor = (
      window as unknown as {
        BarcodeDetector?: new (options: { formats: string[] }) => {
          detect: (source: HTMLVideoElement) => Promise<{ rawValue: string }[]>;
        };
      }
    ).BarcodeDetector;
    if (!DetectorCtor) {
      await alert({
        message: 'Camera scanning is not supported on this device — paste the link instead.',
        variant: 'warning',
      });
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      streamRef.current = stream;
      setScanning(true);
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = stream;
      await video.play();
      const detector = new DetectorCtor({ formats: ['qr_code'] });
      const tick = async () => {
        if (!streamRef.current) return;
        try {
          const codes = await detector.detect(video);
          const token = codes.map((c) => tokenFromScan(c.rawValue)).find(Boolean);
          if (token) {
            stopCamera();
            await handleTokenSubmit(token);
            return;
          }
        } catch {
          // detection errors are transient — keep scanning
        }
        requestAnimationFrame(() => void tick());
      };
      requestAnimationFrame(() => void tick());
    } catch {
      stopCamera();
      await alert({ message: 'Could not access the camera.', variant: 'error' });
    }
  };

  const checkedInCount = attendance?.checkedIn ?? guests.filter((g) => g.checkedInAt).length;
  const totalCount = attendance?.totalGuests ?? guests.length;

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-4" data-testid="check-in-page">
      <div className="flex items-center gap-3">
        <Link
          to={`/events/${eventId}`}
          aria-label="Back to event"
          className="p-2 text-gray-500 hover:text-gray-900 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-gray-900 truncate">Guest Check-In</h1>
          {event?.name && <p className="text-sm text-gray-500 truncate">{event.name}</p>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-primary-600">{checkedInCount}</div>
          <div className="text-xs text-gray-500">Checked in</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{totalCount}</div>
          <div className="text-xs text-gray-500">Total guests</div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 inline-flex items-center gap-1.5">
            <ScanLine className="w-4 h-4 text-gray-400" />
            Scan QR
          </h2>
          <button
            type="button"
            onClick={() => (scanning ? stopCamera() : void startCamera())}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Camera className="w-4 h-4" />
            {scanning ? 'Stop camera' : 'Use camera'}
          </button>
        </div>
        <video
          ref={videoRef}
          className={scanning ? 'w-full rounded-lg' : 'hidden'}
          muted
          playsInline
        />
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void handleTokenSubmit(scanValue);
          }}
        >
          <input
            type="text"
            value={scanValue}
            onChange={(e) => setScanValue(e.target.value)}
            placeholder="Paste RSVP link or code"
            aria-label="RSVP link or code"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
          <button
            type="submit"
            className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            Check in
          </button>
        </form>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search guests…"
            aria-label="Search guests"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        {isLoading ? (
          <p className="text-sm text-gray-500 py-4 text-center">Loading guests…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">No guests found.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((guest) => (
              <li key={guest.id} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 truncate">
                    {[guest.firstName, guest.lastName].filter(Boolean).join(' ')}
                  </div>
                  <div className="text-xs text-gray-500">
                    {guest.tableNumber != null ? `Table ${guest.tableNumber}` : 'No table'}
                    {guest.checkedInAt &&
                      ` · in at ${new Date(guest.checkedInAt).toLocaleTimeString('en-US', {
                        timeStyle: 'short',
                      })}`}
                  </div>
                </div>
                {guest.checkedInAt ? (
                  <button
                    type="button"
                    aria-label={`Undo check-in for ${guest.firstName}`}
                    onClick={() => void handleToggle(guest)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Checked in
                    <Undo2 className="w-3.5 h-3.5 text-green-500" />
                  </button>
                ) : (
                  <button
                    type="button"
                    aria-label={`Check in ${guest.firstName}`}
                    onClick={() => void handleToggle(guest)}
                    className="px-3 py-1.5 text-xs text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                  >
                    Check in
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default CheckInPage;
